import React from 'react';
import { useSocket } from '../../contexts/SocketContext';
import { formatString } from '@deadpan/shared';
import { useTranslations } from '../../hooks/useTranslations';

const SPIN_STEP_MS = 120;

export const BombRelayResults: React.FC = () => {
  const { socket, currentRoom, gameState, myPlayerId } = useSocket();
  const t = useTranslations();
  const [now, setNow] = React.useState(Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 100);
    return () => clearInterval(interval);
  }, []);

  if (!currentRoom || !gameState) {
    return null;
  }

  const explodedId = gameState.bombRelayExplodedPlayerId;
  const explodedPlayer = currentRoom.players.find((player) => player.id === explodedId);

  const relayOrder = gameState.bombRelayOrder || currentRoom.players.map((player) => player.id);
  const candidates = relayOrder
    .filter((playerId) => playerId !== explodedId)
    .map((playerId) => currentRoom.players.find((player) => player.id === playerId))
    .filter((player): player is NonNullable<typeof player> => Boolean(player));

  const draw = gameState.bombRelayDraw;
  const isSpinning = !!draw?.isSpinning;
  const isDrawFinished = !isSpinning && !!draw?.executorId;
  const canDraw = currentRoom.hostId === myPlayerId && !isSpinning && !draw?.executorId;

  const spinIndex = draw
    ? Math.floor(Math.max(0, now - draw.startedAt) / SPIN_STEP_MS) % Math.max(1, candidates.length)
    : 0;
  const spinningPlayer = candidates[spinIndex];

  const executorPlayer = currentRoom.players.find((player) => player.id === draw?.executorId);

  const handleDraw = () => {
    if (!socket) return;
    socket.emit('client:bomb-relay-draw-executor');
  };

  return (
    <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl p-4 sm:p-6 md:p-8 w-[90%] max-w-[600px] border border-white/20 flex flex-col items-center">
      <div className="w-full text-center mb-6">
        <div className="text-6xl mb-3">💥</div>
        <h2 className="text-4xl sm:text-5xl font-extrabold text-red-600 mb-2">
          {formatString(t.games.bombRelay.explodedMessage, {
            name: explodedPlayer?.name || t.games.bombRelay.unknownPlayer,
          })}
        </h2>
      </div>

      <div className="w-full rounded-xl border-2 border-purple-200 bg-purple-50 p-4 sm:p-6 mb-4">
        <h3 className="text-xl font-bold text-purple-900 mb-3 text-center">{t.games.bombRelay.drawTitle}</h3>
        <p className="text-sm text-purple-800 text-center mb-4">
          {t.games.bombRelay.drawDescription}
        </p>

        {(isSpinning || draw?.executorId) && (
          <div className="rounded-xl bg-white border border-purple-200 p-6 text-center mb-4">
            {isSpinning ? (
              <>
                <div className="text-sm text-gray-500 mb-2">{t.games.bombRelay.drawing}</div>
                <div className="text-3xl font-bold text-purple-700">{spinningPlayer?.name || '...'}</div>
              </>
            ) : (
              <>
                <div className="text-sm text-gray-500 mb-2">{t.games.bombRelay.drawResult}</div>
                <div className="text-3xl font-extrabold text-green-600">{executorPlayer?.name || t.games.bombRelay.unknownPlayer}</div>
              </>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
          {candidates.map((player) => (
            <div
              key={player.id}
              className={`rounded-lg px-3 py-2 text-sm font-semibold text-center border ${
                isDrawFinished && player.id === draw?.executorId
                  ? 'bg-green-100 text-green-800 border-green-300'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              {player.name}
            </div>
          ))}
        </div>

        <button
          onClick={handleDraw}
          disabled={!canDraw}
          className="w-full bg-gradient-to-r from-purple-600 to-fuchsia-600 text-white py-3 px-4 rounded-xl font-bold hover:from-purple-700 hover:to-fuchsia-700 disabled:from-gray-400 disabled:to-gray-400 disabled:cursor-not-allowed transition-all"
        >
          {canDraw ? t.games.bombRelay.drawButton : draw?.executorId ? t.games.bombRelay.drawCompleted : t.games.bombRelay.waitHostButton}
        </button>
      </div>
    </div>
  );
};

